import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Transaction } from '../components/Business/FinanceTypes';

export function useTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const updateTransaction = async (id: string, updates: Partial<Transaction>) => {
    try {
      const { error } = await supabase
        .from('business_transactions')
        .update({
          type: updates.type,
          business_name: updates.businessName,
          amount: updates.amount,
          date: updates.date,
          status: updates.status,
          category: updates.category,
          description: updates.description
        })
        .eq('id', id);

      if (error) throw error;
      
      setTransactions(prev => prev.map(t => 
        t.id === id ? { ...t, ...updates } : t
      ));
    } catch (err) {
      console.error('Error updating transaction:', err);
      throw err;
    }
  };

  const deleteTransaction = async (id: string) => {
    try {
      const { error } = await supabase
        .from('business_transactions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTransactions(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      console.error('Error deleting transaction:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('business_transactions')
        .select('*')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .order('date', { ascending: false });

      if (error) throw error;

      setTransactions(data.map(t => ({
        id: t.id,
        type: t.type as 'invoice' | 'expense',
        businessName: t.business_name,
        amount: Number(t.amount),
        date: t.date,
        status: t.status as 'paid' | 'pending' | 'overdue',
        category: t.category,
        description: t.description
      })));
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const addTransaction = async (transaction: Omit<Transaction, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('business_transactions')
        .insert({
          type: transaction.type,
          business_name: transaction.businessName,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          amount: transaction.amount,
          date: transaction.date,
          status: transaction.status,
          category: transaction.category,
          description: transaction.description
        })
        .select()
        .single();

      if (error) throw error;

      const newTransaction: Transaction = {
        id: data.id,
        type: data.type,
        businessName: data.business_name,
        amount: Number(data.amount),
        date: data.date,
        status: data.status,
        category: data.category_id,
        description: data.description
      };

      setTransactions([newTransaction, ...transactions]);
      return newTransaction;
    } catch (err) {
      console.error('Error adding transaction:', err);
      throw err;
    }
  };

  return {
    transactions,
    loading,
    error,
    addTransaction,
    deleteTransaction,
    updateTransaction,
    fetchTransactions
  };
}